import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import "../../css/OrderManagement.css";
import { fetchCustomerFeedback, storeCustomerFeedback } from "../../service/api/orders";

// ─── icons ────────────────────────────────────────────────────────────────────
const SearchIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
);
const ExportIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
        <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
);
const ResetIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="1 4 1 10 7 10"/>
        <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
    </svg>
);
const CalendarIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
);
const ChevronLeft = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="15 18 9 12 15 6"/>
    </svg>
);
const ChevronRight = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="9 18 15 12 9 6"/>
    </svg>
);

// ─── date helpers ─────────────────────────────────────────────────────────────
const MONTHS = ["January","February","March","April","May","June",
    "July","August","September","October","November","December"];
const DAYS = ["Su","Mo","Tu","We","Th","Fr","Sa"];

const toYMD   = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
const fromYMD = (s) => { const [y,m,d] = s.split("-"); return new Date(+y,+m-1,+d); };
const sameDay = (a,b) => a && b && toYMD(a) === toYMD(b);

// ─── DateRangePicker (unchanged, kept intact) ─────────────────────────────────
const DateRangePicker = ({ fromDate, toDate, onChange }) => {
    const [open, setOpen]           = useState(false);
    const [hovered, setHovered]     = useState(null);
    const [selecting, setSelecting] = useState(null);
    const [popupStyle, setPopupStyle] = useState({});
    const today = new Date();
    const [viewYear, setViewYear]   = useState(today.getFullYear());
    const [viewMonth, setViewMonth] = useState(today.getMonth());
    const btnRef = useRef(null);
    const popRef = useRef(null);

    const openPopup = () => {
        if (!btnRef.current) return;
        const rect = btnRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        setPopupStyle(spaceBelow >= 340
            ? { top: rect.bottom + window.scrollY + 8, left: rect.left + window.scrollX }
            : { top: rect.top + window.scrollY - 348,  left: rect.left + window.scrollX }
        );
        setOpen(true);
    };

    useEffect(() => {
        if (!open) return;
        const h = (e) => {
            if (btnRef.current && !btnRef.current.contains(e.target) &&
                popRef.current  && !popRef.current.contains(e.target))
            { setOpen(false); setSelecting(null); }
        };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, [open]);

    const prevMonth = () => viewMonth === 0 ? (setViewMonth(11), setViewYear(y=>y-1)) : setViewMonth(m=>m-1);
    const nextMonth = () => viewMonth===11  ? (setViewMonth(0),  setViewYear(y=>y+1)) : setViewMonth(m=>m+1);

    const handleDayClick = (date) => {
        if (!selecting) { setSelecting(date); onChange({ from: toYMD(date), to: "" }); }
        else {
            const [from, to] = date < selecting ? [date, selecting] : [selecting, date];
            onChange({ from: toYMD(from), to: toYMD(to) });
            setSelecting(null); setOpen(false);
        }
    };

    const buildDays = (y, m) => {
        const first = new Date(y, m, 1).getDay();
        const total = new Date(y, m+1, 0).getDate();
        const cells = [];
        for (let i = 0; i < first; i++) cells.push(null);
        for (let d = 1; d <= total; d++) cells.push(new Date(y, m, d));
        return cells;
    };

    const fromD = fromDate ? fromYMD(fromDate) : null;
    const toD   = toDate   ? fromYMD(toDate)   : null;

    const dayStyle = (date) => {
        if (!date) return {};
        const [rFrom, rTo] = selecting
            ? (hovered && hovered < selecting ? [hovered, selecting] : [selecting, hovered])
            : [fromD, toD];
        const isStart = sameDay(date, fromD) || (selecting && sameDay(date, selecting));
        const isEnd   = !selecting && sameDay(date, toD);
        const inRange = rFrom && rTo && date > rFrom && date < rTo;
        const isToday = sameDay(date, today);
        if (isStart || isEnd) return { background:"#2563eb", color:"#fff", fontWeight:700, borderRadius:"50%" };
        if (inRange)          return { background:"#dbeafe", color:"#1e40af", borderRadius:0 };
        if (isToday)          return { fontWeight:700, borderRadius:"50%" };
        return { borderRadius:"50%" };
    };

    const cells = buildDays(viewYear, viewMonth);
    const label = fromDate && toDate ? `${fromDate}  →  ${toDate}` : fromDate ? `${fromDate}  →  …` : "Select date range";

    const popup = open && ReactDOM.createPortal(
        <div ref={popRef} style={{ position:"absolute", ...popupStyle, zIndex:99999, background:"#fff", borderRadius:14, boxShadow:"0 8px 32px rgba(0,0,0,0.18)", padding:18, border:"1px solid #e2e8f0", width:260 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                <button onClick={prevMonth} style={{ border:"none", background:"none", cursor:"pointer", padding:4, color:"#64748b" }}><ChevronLeft/></button>
                <span style={{ fontWeight:700, fontSize:13, color:"#1e293b" }}>{MONTHS[viewMonth]} {viewYear}</span>
                <button onClick={nextMonth} style={{ border:"none", background:"none", cursor:"pointer", padding:4, color:"#64748b" }}><ChevronRight/></button>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", marginBottom:4 }}>
                {DAYS.map(d => <div key={d} style={{ textAlign:"center", fontSize:10, color:"#94a3b8", fontWeight:600, padding:"2px 0" }}>{d}</div>)}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2 }}>
                {cells.map((date, i) => (
                    <div key={i} onClick={date ? () => handleDayClick(date) : undefined}
                         onMouseEnter={date ? () => setHovered(date) : undefined}
                         onMouseLeave={() => setHovered(null)}
                         style={{ height:32, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, cursor:date?"pointer":"default", userSelect:"none", ...(date ? dayStyle(date) : {}) }}>
                        {date ? date.getDate() : ""}
                    </div>
                ))}
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:12, borderTop:"1px solid #f1f5f9", paddingTop:10 }}>
                <span style={{ fontSize:11, color:"#94a3b8" }}>{selecting ? "Pick end date" : fromDate && toDate ? `${fromDate} → ${toDate}` : "Pick start date"}</span>
                <button onClick={() => { onChange({ from:"", to:"" }); setSelecting(null); setOpen(false); }}
                        style={{ fontSize:11, color:"#ef4444", background:"none", border:"none", cursor:"pointer", fontWeight:600 }}>Clear</button>
            </div>
        </div>,
        document.body
    );

    return (
        <div style={{ position:"relative" }}>
            <button ref={btnRef} type="button" onClick={() => open ? setOpen(false) : openPopup()}
                    style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 14px", borderRadius:10, border:"1px solid #e2e8f0", background:fromDate?"#eff6ff":"#fff", color:fromDate?"#2563eb":"#64748b", fontSize:13, cursor:"pointer", whiteSpace:"nowrap", fontWeight:fromDate?600:400 }}>
                <CalendarIcon/> {label}
            </button>
            {popup}
        </div>
    );
};

const SOURCE_OPTIONS = [
    { value: "delivery_guy", label: "Delivery Guy" },
    { value: "restaurant", label: "Restaurant" },
    { value: "order_manager", label: "Order Manager" },
    { value: "field_manager", label: "Field Manager" },
    { value: "custom", label: "Custom" },
];

const TARGET_OPTIONS = [
    { value: "order_manager", label: "Order Manager" },
    { value: "field_manager", label: "Field Manager" },
];

const PRIORITY_OPTIONS = [
    { value: "normal", label: "Normal" },
    { value: "high", label: "High" },
    { value: "urgent", label: "Urgent" },
];

const emptyFeedbackItem = () => ({
    source: "",
    target: [],
    rating: "",
    review: "",
});


const StarRatingText = ({ rating }) => {
    if (rating === null || rating === undefined || rating === "") {
        return <span style={{ color: "#94a3b8", fontSize: 13 }}>No rating</span>;
    }

    const value = Number(rating);
    const fullStars = Math.max(0, Math.min(5, Math.round(value)));

    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ color: "#f59e0b", letterSpacing: 1, fontSize: 14 }}>
                {"★".repeat(fullStars)}{"☆".repeat(5 - fullStars)}
            </span>
            <strong style={{ color: "#0f172a", fontSize: 13 }}>{value}/5</strong>
        </span>
    );
};

const ReviewBlock = ({ title, rating, review }) => {
    const hasRating = rating !== null && rating !== undefined && rating !== "";
    const hasReview = review && String(review).trim();

    if (!hasRating && !hasReview) return null;

    return (
        <div
            style={{
                background: "#fff",
                border: "1px solid #fde68a",
                borderRadius: 12,
                padding: 12,
            }}
        >
            <div style={{ fontSize: 12, fontWeight: 800, color: "#475569", marginBottom: 7 }}>
                {title}
            </div>

            <div style={{ marginBottom: 8 }}>
                <StarRatingText rating={rating} />
            </div>

            <div
                style={{
                    fontSize: 13,
                    color: hasReview ? "#334155" : "#94a3b8",
                    lineHeight: 1.45,
                    background: hasReview ? "#f8fafc" : "transparent",
                    border: hasReview ? "1px solid #e2e8f0" : "none",
                    borderRadius: hasReview ? 10 : 0,
                    padding: hasReview ? "8px 10px" : 0,
                    whiteSpace: "normal",
                }}
            >
                {hasReview ? `*** ${review}` : "No review"}
            </div>
        </div>
    );
};


const ExistingAppRatingsBlock = ({ deliveryRating }) => {
    const hasAnyRatingOrReview = deliveryRating && [
        deliveryRating.customer_to_store_rating,
        deliveryRating.customer_to_store_review,

        deliveryRating.customer_rating,
        deliveryRating.customer_review,

        deliveryRating.store_rating,
        deliveryRating.store_review,

        deliveryRating.delivery_to_customer_rating,
        deliveryRating.delivery_to_customer_review,
    ].some(v => v !== null && v !== undefined && String(v).trim() !== "");

    if (!hasAnyRatingOrReview) {
        return (
            <div
                style={{
                    marginBottom: 18,
                    padding: 14,
                    borderRadius: 14,
                    border: "1px solid #e2e8f0",
                    background: "#f8fafc",
                    color: "#64748b",
                    fontSize: 13,
                    fontWeight: 600,
                }}
            >
                No app rating/review found for this order.
            </div>
        );
    }

    return (
        <div
            style={{
                marginBottom: 18,
                padding: 16,
                borderRadius: 14,
                border: "1px solid #fde68a",
                background: "#fffbeb",
            }}
        >
            <div style={{ marginBottom: 12 }}>
                <div
                    style={{
                        fontSize: 12,
                        fontWeight: 800,
                        color: "#92400e",
                        textTransform: "uppercase",
                    }}
                >
                    Existing App Ratings / Reviews
                </div>

                <div style={{ fontSize: 13, color: "#78350f", marginTop: 2 }}>
                    Only for reference. Ask questions around these reviews if needed.
                </div>
            </div>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 12,
                }}
            >
                <ReviewBlock
                    title="Customer → Store"
                    rating={deliveryRating.customer_to_store_rating}
                    review={deliveryRating.customer_to_store_review}
                />

                <ReviewBlock
                    title="Customer → Delivery Guy"
                    rating={deliveryRating.customer_rating}
                    review={deliveryRating.customer_review}
                />

                <ReviewBlock
                    title="Delivery Guy → Store"
                    rating={deliveryRating.store_rating}
                    review={deliveryRating.store_review}
                />

                <ReviewBlock
                    title="Delivery Guy → Customer"
                    rating={deliveryRating.delivery_to_customer_rating}
                    review={deliveryRating.delivery_to_customer_review}
                />
            </div>
        </div>
    );
};


const CustomerOrderSummaryBlock = ({ summary }) => {
    const successfulOrders = Number(summary?.successful_orders ?? 0);
    const failedOrders = Number(summary?.failed_orders ?? 0);
    const successfulOrderTotal = Number(summary?.successful_order_total ?? 0);

    const itemStyle = {
        display: "flex",
        alignItems: "center",
        gap: 6,
        fontSize: 13,
        fontWeight: 700,
        color: "#334155",
    };

    return (
        <div
            style={{
                marginBottom: 12,
                padding: "10px 12px",
                borderRadius: 12,
                border: "1px solid #dbeafe",
                background: "#eff6ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
            }}
        >
            <div style={{ fontSize: 12, fontWeight: 900, color: "#1d4ed8" }}>
                Customer Summary
            </div>

            <div style={itemStyle}>
                ✅ Success: <span style={{ color: "#15803d" }}>{successfulOrders}</span>
            </div>

            <div style={itemStyle}>
                ❌ Failed: <span style={{ color: "#dc2626" }}>{failedOrders}</span>
            </div>

            <div style={itemStyle}>
                💰 Total:{" "}
                <span style={{ color: "#0f172a" }}>
                    ₹{successfulOrderTotal.toLocaleString("en-IN", {
                    maximumFractionDigits: 2,
                })}
                </span>
            </div>
        </div>
    );
};

const FeedbackModal = ({
                           open,
                           item,
                           attended,
                           submitting,
                           onClose,
                           onSubmit,
                       }) => {
    const [mainRating, setMainRating] = useState("");
    const [mainReview, setMainReview] = useState("");
    const [isEscalated, setIsEscalated] = useState(false);
    const [priority, setPriority] = useState("normal");
    const [items, setItems] = useState([emptyFeedbackItem()]);
    const [doNotCallAgain, setDoNotCallAgain] = useState(false);


    const deliveryRating = item?.delivery_rating;
    const customerOrderSummary = item?.customer_order_summary;

    useEffect(() => {
        if (!open) return;

        // Important:
        // Existing app ratings/reviews are only shown as reference.
        // They should NOT influence customer_feedback form values.
        setMainRating("");
        setMainReview("");
        setIsEscalated(false);
        setPriority("normal");
        setItems([emptyFeedbackItem()]);
        setDoNotCallAgain(false);
    }, [open, item?.id]);

    if (!open || !item) return null;


    const updateItem = (index, key, value) => {
        setItems(prev => prev.map((row, i) => (
            i === index ? { ...row, [key]: value } : row
        )));
    };

    const toggleTarget = (index, targetValue) => {
        setItems(prev => prev.map((row, i) => {
            if (i !== index) return row;

            const current = Array.isArray(row.target) ? row.target : [];
            const exists = current.includes(targetValue);

            return {
                ...row,
                target: exists
                    ? current.filter(v => v !== targetValue)
                    : [...current, targetValue],
            };
        }));
    };

    const addItem = () => {
        setItems(prev => [...prev, emptyFeedbackItem()]);
    };

    const removeItem = (index) => {
        setItems(prev => prev.length === 1
            ? [emptyFeedbackItem()]
            : prev.filter((_, i) => i !== index)
        );
    };

    const handleSubmit = () => {
        const cleanedItems = items
            .map(row => ({
                source: row.source,
                target: Array.isArray(row.target) ? row.target : [],
                rating: row.rating !== "" && row.rating !== null && row.rating !== undefined
                    ? Number(row.rating)
                    : null,
                review: row.review?.trim() ?? "",
            }))
            .filter(row => row.source || row.target.length || row.rating !== null || row.review);

        if (!mainReview.trim() && cleanedItems.length === 0) {
            alert("Please enter feedback or add at least one role-based feedback.");
            return;
        }

        const invalidItem = cleanedItems.find(row => !row.source);
        if (invalidItem) {
            alert("Please select source for every role feedback item.");
            return;
        }

        onSubmit({
            rating: mainRating !== "" ? Number(mainRating) : null,
            feedback_payload: mainReview.trim(),
            feedback_meta: {
                is_escalated: Boolean(isEscalated),
                priority: isEscalated ? priority : "normal",
                items: cleanedItems,
                do_not_call_again: Boolean(doNotCallAgain),
                do_not_call: Boolean(doNotCallAgain),
            },
        });
    };

    return ReactDOM.createPortal(
        <div
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(15, 23, 42, 0.55)",
                zIndex: 999999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 20,
            }}
            onMouseDown={() => {
                if (!submitting) onClose();
            }}
        >
            <div
                onMouseDown={e => e.stopPropagation()}
                style={{
                    width: "min(920px, 96vw)",
                    maxHeight: "90vh",
                    overflowY: "auto",
                    background: "#fff",
                    borderRadius: 18,
                    boxShadow: "0 24px 80px rgba(15,23,42,0.35)",
                    border: "1px solid #e2e8f0",
                }}
            >
                <div style={{
                    padding: "18px 22px",
                    borderBottom: "1px solid #e2e8f0",
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 14,
                    alignItems: "flex-start",
                }}>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>
                            Customer Feedback
                        </div>
                        <h2 style={{ margin: "4px 0 0", fontSize: 20, color: "#0f172a" }}>
                            #{item?.unique_order_id?.slice(-4)} · {item?.user?.name ?? "-"}
                        </h2>
                        <div style={{ marginTop: 5, fontSize: 13, color: "#64748b" }}>
                            {item?.user?.phone ?? "-"} · {attended ? "Attended" : "No Answer"}
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        disabled={submitting}
                        style={{
                            border: "none",
                            background: "#f1f5f9",
                            color: "#334155",
                            borderRadius: 10,
                            padding: "8px 11px",
                            cursor: submitting ? "not-allowed" : "pointer",
                            fontWeight: 700,
                        }}
                    >
                        ✕
                    </button>
                </div>

                <div style={{ padding: 22 }}>

                    <CustomerOrderSummaryBlock summary={customerOrderSummary} />
                    <ExistingAppRatingsBlock deliveryRating={deliveryRating} />

                    <div style={{
                        display: "grid",
                        gridTemplateColumns: "120px 1fr",
                        gap: 14,
                        marginBottom: 18,
                    }}>
                        <div>
                            <label style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>
                                Overall Rating
                            </label>
                            <input
                                type="number"
                                min="0"
                                max="5"
                                step="0.1"
                                placeholder="0–5"
                                value={mainRating}
                                onChange={e => setMainRating(e.target.value)}
                                style={{
                                    marginTop: 6,
                                    width: "100%",
                                    border: "1px solid #cbd5e1",
                                    borderRadius: 10,
                                    padding: "9px 10px",
                                    fontSize: 14,
                                    outline: "none",
                                }}
                            />
                        </div>

                        <div>
                            <label style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>
                                Main Feedback
                            </label>
                            <textarea
                                rows={3}
                                placeholder="Customer feedback summary..."
                                value={mainReview}
                                onChange={e => setMainReview(e.target.value)}
                                style={{
                                    marginTop: 6,
                                    width: "100%",
                                    border: "1px solid #cbd5e1",
                                    borderRadius: 10,
                                    padding: 10,
                                    resize: "vertical",
                                    fontFamily: "inherit",
                                    fontSize: 14,
                                    outline: "none",
                                }}
                            />
                        </div>
                    </div>

                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 14,
                        padding: 14,
                        border: "1px solid #e2e8f0",
                        borderRadius: 14,
                        background: isEscalated ? "#fef2f2" : "#f8fafc",
                        marginBottom: 18,
                    }}>
                        <label style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            cursor: "pointer",
                            fontSize: 14,
                            fontWeight: 700,
                            color: isEscalated ? "#b91c1c" : "#334155",
                        }}>
                            <input
                                type="checkbox"
                                checked={isEscalated}
                                onChange={e => {
                                    const checked = e.target.checked;
                                    setIsEscalated(checked);
                                    setPriority(checked ? "high" : "normal");
                                }}
                                style={{ width: 17, height: 17, accentColor: "#ef4444" }}
                            />
                            Escalate this feedback
                        </label>

                        <select
                            value={priority}
                            disabled={!isEscalated}
                            onChange={e => setPriority(e.target.value)}
                            style={{
                                minWidth: 140,
                                padding: "8px 12px",
                                borderRadius: 10,
                                border: "1px solid #cbd5e1",
                                background: !isEscalated ? "#f1f5f9" : "#fff",
                                color: !isEscalated ? "#94a3b8" : "#0f172a",
                                fontWeight: 700,
                            }}
                        >
                            {PRIORITY_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 14,
                        padding: 14,
                        border: doNotCallAgain ? "1px solid #fcd34d" : "1px solid #e2e8f0",
                        borderRadius: 14,
                        background: doNotCallAgain ? "#fffbeb" : "#f8fafc",
                        marginBottom: 18,
                    }}>
                        <label style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            cursor: "pointer",
                            fontSize: 14,
                            fontWeight: 700,
                            color: doNotCallAgain ? "#92400e" : "#334155",
                        }}>
                            <input
                                type="checkbox"
                                checked={doNotCallAgain}
                                onChange={e => setDoNotCallAgain(e.target.checked)}
                                style={{ width: 17, height: 17, accentColor: "#f59e0b" }}
                            />
                            Do Not Call Again
                        </label>

                        <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>
                            Customer will be excluded from future feedback calls.
                        </span>
                    </div>

                    <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 10,
                    }}>
                        <h3 style={{ margin: 0, fontSize: 16, color: "#0f172a" }}>
                            Role Based Feedback
                        </h3>

                        <button
                            type="button"
                            onClick={addItem}
                            style={{
                                border: "1px solid #bfdbfe",
                                background: "#eff6ff",
                                color: "#2563eb",
                                fontWeight: 700,
                                borderRadius: 10,
                                padding: "8px 12px",
                                cursor: "pointer",
                            }}
                        >
                            + Add Role
                        </button>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                        {items.map((row, index) => (
                            <div
                                key={index}
                                style={{
                                    border: "1px solid #e2e8f0",
                                    borderRadius: 14,
                                    padding: 14,
                                    background: "#fff",
                                }}
                            >
                                <div style={{
                                    display: "grid",
                                    gridTemplateColumns: "180px 120px 1fr 40px",
                                    gap: 12,
                                    alignItems: "start",
                                }}>
                                    <div>
                                        <label style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>
                                            Source
                                        </label>
                                        <select
                                            value={row.source}
                                            onChange={e => updateItem(index, "source", e.target.value)}
                                            style={{
                                                marginTop: 6,
                                                width: "100%",
                                                padding: "9px 10px",
                                                borderRadius: 10,
                                                border: "1px solid #cbd5e1",
                                                outline: "none",
                                            }}
                                        >
                                            <option value="">Select source</option>
                                            {SOURCE_OPTIONS.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>
                                            Rating
                                        </label>
                                        <input
                                            type="number"
                                            min="0"
                                            max="5"
                                            step="0.1"
                                            placeholder="0–5"
                                            value={row.rating}
                                            onChange={e => updateItem(index, "rating", e.target.value)}
                                            style={{
                                                marginTop: 6,
                                                width: "100%",
                                                padding: "9px 10px",
                                                borderRadius: 10,
                                                border: "1px solid #cbd5e1",
                                                outline: "none",
                                            }}
                                        />
                                    </div>

                                    <div>
                                        <label style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>
                                            Target / Responsible
                                        </label>
                                        <div style={{
                                            marginTop: 6,
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 8,
                                        }}>
                                            {TARGET_OPTIONS.map(opt => {
                                                const checked = row.target?.includes(opt.value);
                                                return (
                                                    <label
                                                        key={opt.value}
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            gap: 6,
                                                            padding: "6px 9px",
                                                            borderRadius: 999,
                                                            border: checked ? "1px solid #2563eb" : "1px solid #cbd5e1",
                                                            background: checked ? "#eff6ff" : "#fff",
                                                            color: checked ? "#1d4ed8" : "#475569",
                                                            fontSize: 12,
                                                            fontWeight: 700,
                                                            cursor: "pointer",
                                                        }}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={() => toggleTarget(index, opt.value)}
                                                            style={{ accentColor: "#2563eb" }}
                                                        />
                                                        {opt.label}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => removeItem(index)}
                                        style={{
                                            marginTop: 22,
                                            width: 34,
                                            height: 34,
                                            borderRadius: 10,
                                            border: "1px solid #fecaca",
                                            background: "#fef2f2",
                                            color: "#dc2626",
                                            cursor: "pointer",
                                            fontWeight: 800,
                                        }}
                                    >
                                        ×
                                    </button>
                                </div>

                                <div style={{ marginTop: 12 }}>
                                    <label style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>
                                        Review
                                    </label>
                                    <textarea
                                        rows={2}
                                        placeholder="Example: Rider delayed pickup / Restaurant preparation delay..."
                                        value={row.review}
                                        onChange={e => updateItem(index, "review", e.target.value)}
                                        style={{
                                            marginTop: 6,
                                            width: "100%",
                                            border: "1px solid #cbd5e1",
                                            borderRadius: 10,
                                            padding: 10,
                                            resize: "vertical",
                                            fontFamily: "inherit",
                                            fontSize: 14,
                                            outline: "none",
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div style={{
                    padding: "16px 22px",
                    borderTop: "1px solid #e2e8f0",
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 10,
                    background: "#f8fafc",
                    borderBottomLeftRadius: 18,
                    borderBottomRightRadius: 18,
                }}>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={submitting}
                        className="btn"
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="btn"
                        style={{
                            background: "#2563eb",
                            color: "#fff",
                            border: "none",
                            opacity: submitting ? 0.65 : 1,
                            cursor: submitting ? "not-allowed" : "pointer",
                        }}
                    >
                        {submitting ? "Submitting…" : "Submit Feedback"}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

// ─── main component ───────────────────────────────────────────────────────────
const CustomerFeedbackDashboard = () => {
    const [orders, setOrders]         = useState([]);
    const [loading, setLoading]       = useState(false);
    const [submitting, setSubmitting] = useState({});

    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage]       = useState(1);
    const [total, setTotal]             = useState(0);
    const PER_PAGE = 100;

    const [search, setSearch]     = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate]     = useState("");

    const [feedbackModal, setFeedbackModal] = useState({
        open: false,
        item: null,
    });

    // ── is_attended: defaults to true (1) for every row ──────────────────────
    const [isAttended, setIsAttended] = useState({}); // { [order_id]: boolean }

    const loadData = async (page = 1, opts = {}) => {
        try {
            setLoading(true);
            const resolvedSearch   = "search"    in opts ? opts.search    : search;
            const resolvedFromDate = "from_date" in opts ? opts.from_date : fromDate;
            const resolvedToDate   = "to_date"   in opts ? opts.to_date   : toDate;

            const params = {
                page, per_page: PER_PAGE,
                ...(resolvedSearch   ? { search:    resolvedSearch }   : {}),
                ...(resolvedFromDate ? { from_date: resolvedFromDate } : {}),
                ...(resolvedToDate   ? { to_date:   resolvedToDate }   : {}),
            };

            const response  = await fetchCustomerFeedback(params);
            const paginated = response?.data ?? {};
            const rows      = paginated?.data ?? [];

            setOrders(rows);
            setCurrentPage(paginated?.current_page ?? 1);
            setLastPage(paginated?.last_page ?? 1);
            setTotal(paginated?.total ?? 0);

            // Default every new row to attended = true
            setIsAttended(prev => {
                const next = { ...prev };
                rows.forEach(r => { if (!(r.id in next)) next[r.id] = true; });
                return next;
            });
        } catch (err) {
            console.error(err);
            alert("Failed to fetch feedback data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(1, { search:"", from_date:"", to_date:"" }); }, []);

    const handleDateChange = ({ from, to }) => {
        setFromDate(from); setToDate(to);
        loadData(1, { from_date: from, to_date: to });
    };
    const handleSearch = () => loadData(1, { search, from_date: fromDate, to_date: toDate });
    const handleReset  = () => {
        setSearch(""); setFromDate(""); setToDate("");
        loadData(1, { search:"", from_date:"", to_date:"" });
    };

    const parseLocation = (str) => { try { return JSON.parse(str ?? "{}"); } catch { return {}; } };
    const formatAddress = (item) => {
        const loc = parseLocation(item.location);
        return [loc.house, loc.address].filter(Boolean).join(", ") || item.address || "-";
    };
    const formatDate = (str) => str ? new Date(str).toLocaleString("en-IN", { hour12: true }) : "-";

    // ── submit — now sends is_attended ────────────────────────────────────────
    const handleSubmit = async (payload) => {
        const item = feedbackModal.item;
        if (!item) return;

        try {
            setSubmitting(p => ({ ...p, [item.id]: true }));

            await storeCustomerFeedback({
                order_id: item.id,
                user_id: item?.user?.id,

                rating: payload.rating,
                feedback_payload: payload.feedback_payload,
                is_attended: isAttended[item.id] ? 1 : 0,

                feedback_meta: payload.feedback_meta,
            });

            setOrders(p => p.filter(o => o.id !== item.id));
            setTotal(t => Math.max(0, t - 1));

            setIsAttended(p => {
                const n = { ...p };
                delete n[item.id];
                return n;
            });

            setFeedbackModal({
                open: false,
                item: null,
            });

            alert("Feedback submitted successfully!");
        } catch (err) {
            console.error(err);
            alert(err?.response?.data?.message || "Failed to submit feedback. Please try again.");
        } finally {
            setSubmitting(p => ({ ...p, [item.id]: false }));
        }
    };

    const handleExport = () => {
        const headers = [
            "Order ID",
            "Unique Order ID",
            "Customer Name",
            "Customer Phone",
            "Payment Mode",
            "Sub Total",
            "Total",
            "Delivery Charge",
            "Distance (km)",
            "Address",
            "Created At",
            "Attended"
        ];
        const rows = orders.map(item => [
            item.id, item.unique_order_id, item?.user?.name ?? "", item?.user?.phone ?? "",
            item.payment_mode, item.sub_total, item.total, item.delivery_charge,
            item.distance, formatAddress(item), item.created_at,
            isAttended[item.id] ? "Yes" : "No",
        ]);
        const csv  = [headers,...rows].map(r => r.map(v => `"${String(v??'').replace(/"/g,'""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type:"text/csv" });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement("a");
        a.href=url; a.download="customer-feedback-pending.csv"; a.click();
        URL.revokeObjectURL(url);
    };

    const goTo = (page) => {
        if (page >= 1 && page <= lastPage) { loadData(page); window.scrollTo({ top:0, behavior:"smooth" }); }
    };

    return (
        <div className="order-page">

            {/* HEADER */}
            <div className="page-header">
                <div>
                    <p className="page-header__eyebrow">Customer Support</p>
                    <h1 className="page-header__title">
                        Customer Feedback Dashboard
                        {total > 0 && <span style={{ fontSize:14, fontWeight:400, marginLeft:10, color:"#64748b" }}>({total.toLocaleString()} pending)</span>}
                    </h1>
                </div>
                <div className="page-header__actions">
                    <button className="btn" onClick={handleExport}><ExportIcon /> Export CSV</button>
                    <button className="btn" onClick={handleReset} style={{ color:"#ef4444", borderColor:"#fca5a5", background:"#fef2f2" }}>
                        <ResetIcon /> Reset Filters
                    </button>
                </div>
            </div>

            {/* FILTERS */}
            <div className="card" style={{ marginBottom:20, padding:16 }}>
                <div style={{ display:"flex", flexWrap:"wrap", alignItems:"center", gap:14 }}>
                    <div className="om-search-wrap" style={{ flex:"1 1 220px" }}>
                        <span className="om-search-icon"><SearchIcon /></span>
                        <input className="om-search-input" placeholder="Search customer / order ID / phone"
                               value={search} onChange={e => setSearch(e.target.value)}
                               onKeyDown={e => e.key === "Enter" && handleSearch()} />
                    </div>
                    <DateRangePicker fromDate={fromDate} toDate={toDate} onChange={handleDateChange} />
                    <button className="btn" onClick={handleSearch}>Search</button>
                </div>
                {(fromDate || toDate) && (
                    <div style={{ marginTop:10, display:"flex", gap:8, flexWrap:"wrap" }}>
                        {fromDate && <span style={{ fontSize:12, background:"#eff6ff", color:"#2563eb", padding:"3px 10px", borderRadius:20, fontWeight:600 }}>From: {fromDate}</span>}
                        {toDate   && <span style={{ fontSize:12, background:"#eff6ff", color:"#2563eb", padding:"3px 10px", borderRadius:20, fontWeight:600 }}>To: {toDate}</span>}
                    </div>
                )}
            </div>

            {/* TABLE */}
            <div className="card" style={{ overflow:"hidden", padding:0 }}>
                <div className="om-table-wrap">
                    <table className="om-table">
                        <thead className="om-thead">
                        <tr>
                            <th className="om-th">Customer</th>
                            <th className="om-th">Order ID</th>
                            <th className="om-th">Address</th>
                            <th className="om-th">Distance</th>
                            <th className="om-th">Total</th>
                            <th className="om-th">Payment</th>
                            <th className="om-th">Ordered At</th>
                            <th className="om-th">Phone</th>
                            {/* ── new column ── */}
                            <th className="om-th" style={{ whiteSpace:"nowrap" }}>Attended?</th>
                            <th className="om-th">Action</th>
                        </tr>
                        </thead>
                        <tbody className="om-tbody">
                        {loading ? (
                            <tr><td colSpan={10}><div className="om-empty">Loading…</div></td></tr>
                        ) : orders.length === 0 ? (
                            <tr><td colSpan={10}><div className="om-empty">No pending feedback orders found.</div></td></tr>
                        ) : (
                            orders.map(item => {
                                const notAttended = !isAttended[item.id];
                                return (
                                    <tr key={item.id} style={item.previously_called ? { background:"#fffbeb" } : undefined}>

                                        {/* CUSTOMER */}
                                        <td className="om-td">
                                            <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                                                <strong>{item?.user?.name ?? "-"}</strong>
                                                {item.previously_called && (
                                                    <span style={{ display:"inline-flex", alignItems:"center", gap:4, fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:20, background:"#fef3c7", color:"#92400e", border:"1px solid #fcd34d", whiteSpace:"nowrap", width:"fit-content" }}>
                                                        📞 Called · {new Date(item.last_called_at).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" })}
                                                    </span>
                                                )}
                                                {/* ── not-answered badge ── */}
                                                {item.last_call_was_not_attended && (
                                                    <span style={{ display:"inline-flex", alignItems:"center", gap:4, fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:20, background:"#fee2e2", color:"#991b1b", border:"1px solid #fca5a5", whiteSpace:"nowrap", width:"fit-content" }}>
                                                        ☎ Not Answered
                                                    </span>
                                                )}
                                            </div>
                                        </td>

                                        {/* ORDER ID */}
                                        <td className="om-td">
                                            <div style={{ fontWeight:600 }}>#{item.unique_order_id?.slice(-4)}</div>
                                        </td>

                                        {/* ADDRESS */}
                                        <td className="om-td" style={{ maxWidth:220 }}>
                                            <div style={{ fontSize:12, whiteSpace:"normal", lineHeight:1.4 }}>{formatAddress(item)}</div>
                                        </td>

                                        {/* DISTANCE */}
                                        <td className="om-td">{item.distance ? `${Number(item.distance).toFixed(2)} km` : "-"}</td>

                                        {/* TOTAL */}
                                        <td className="om-td" style={{ fontWeight:600 }}>₹{item.total}</td>

                                        {/* PAYMENT */}
                                        <td className="om-td">
                                            <span style={{ fontSize:11, padding:"2px 7px", borderRadius:10, fontWeight:600, background:item.payment_mode==="COD"?"#e0f2fe":"#f0fdf4", color:item.payment_mode==="COD"?"#0369a1":"#15803d" }}>
                                                {item.payment_mode}
                                            </span>
                                        </td>

                                        {/* ORDERED AT */}
                                        <td className="om-td" style={{ fontSize:12, whiteSpace:"nowrap" }}>{formatDate(item.created_at)}</td>

                                        {/* PHONE */}
                                        <td className="om-td"><div style={{ fontSize:14 }}>{item?.user?.phone ?? "-"}</div></td>

                                        {/* ── ATTENDED checkbox ── */}
                                        <td className="om-td">
                                            <label style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:5, cursor:"pointer", userSelect:"none" }}>
                                                <input
                                                    type="checkbox"
                                                    checked={isAttended[item.id] ?? true}
                                                    onChange={e => setIsAttended(p => ({ ...p, [item.id]: e.target.checked }))}
                                                    style={{ width:16, height:16, accentColor:"#10b981", cursor:"pointer" }}
                                                />
                                                <span style={{ fontSize:10, fontWeight:700, color: notAttended ? "#ef4444" : "#10b981" }}>
                                                    {notAttended ? "No Answer" : "Attended"}
                                                </span>
                                            </label>
                                        </td>

                                        {/* ACTION */}
                                        <td className="om-td">
                                            <button
                                                className="btn"
                                                onClick={() => setFeedbackModal({open: true, item})}
                                                disabled={submitting[item.id]}
                                                style={{
                                                    background: "#2563eb",
                                                    color: "#fff",
                                                    border: "none",
                                                    opacity: submitting[item.id] ? 0.6 : 1,
                                                    cursor: submitting[item.id] ? "not-allowed" : "pointer"
                                                }}
                                            >
                                                {submitting[item.id] ? "Submitting…" : "Add Feedback"}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION */}
                {!loading && lastPage > 1 && (
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end", gap:8, padding:"12px 16px", borderTop:"1px solid #f1f5f9" }}>
                        <button className="btn" onClick={() => goTo(currentPage-1)} disabled={currentPage===1}>← Prev</button>
                        <span style={{ fontSize:13, color:"#64748b" }}>Page {currentPage} of {lastPage}</span>
                        <button className="btn" onClick={() => goTo(currentPage+1)} disabled={currentPage===lastPage}>Next →</button>
                    </div>
                )}

                <FeedbackModal
                    open={feedbackModal.open}
                    item={feedbackModal.item}
                    attended={feedbackModal.item ? (isAttended[feedbackModal.item.id] ?? true) : true}
                    submitting={feedbackModal.item ? submitting[feedbackModal.item.id] : false}
                    onClose={() => {
                        if (feedbackModal.item && submitting[feedbackModal.item.id]) return;
                        setFeedbackModal({ open: false, item: null });
                    }}
                    onSubmit={handleSubmit}
                />
            </div>
        </div>
    );
};

export default CustomerFeedbackDashboard;